import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { merge, of } from 'rxjs';
import { catchError, debounceTime, filter, map, startWith, switchMap } from 'rxjs/operators';

import { UserPageMergeConflictDialogActions } from 'src/app/users/user-page/user-page-merge-conflict-dialog/user-page-merge-conflict-dialog.actions';
import { UserPageUserGQL } from 'src/generated/graphql.generated';

import { UserPageSimilarCustomersActions } from './user-page-similar-customers.actions';
import { userPageSimilarCustomersFeature } from './user-page-similar-customers.feature';
import { detectMergeConflicts } from './user-page-similar-customers.selectors';

export const searchSimilarCustomers = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    userPageUserGQL = inject(UserPageUserGQL),
) => {

    const tabOpened$ = actions$.pipe(
      ofType(UserPageSimilarCustomersActions.tabOpened),
    );

    const searchInputChanged$ = actions$.pipe(
      ofType(UserPageSimilarCustomersActions.searchInputChanged),
      debounceTime(750),
    );

    const mergeUsersSuccess$ = actions$.pipe(
      ofType(UserPageMergeConflictDialogActions.mergeUsersSuccess),
    );

    return merge(tabOpened$, searchInputChanged$, mergeUsersSuccess$).pipe(
      concatLatestFrom(() => store.select(userPageSimilarCustomersFeature.selectUserPageSimilarCustomersState)),
      filter(([ _action, { sourceUser } ]) => Boolean(sourceUser)),
      switchMap(([ _, { sourceUser, search } ]) => {
        const query = search.trim() || [
          sourceUser.givenName, sourceUser.familyName, sourceUser.email, sourceUser.phone,
        ].filter(Boolean).join(' ');

        return userPageUserGQL.fetch({ filter: { search: query, roles: sourceUser.roleIds } }, { fetchPolicy: 'network-only' }).pipe(
          map((res) => UserPageSimilarCustomersActions.searchSimilarCustomersSuccess({
              customers: res.data?.usersv2.users || [],
          })),
          catchError((error) => of(UserPageSimilarCustomersActions.searchSimilarCustomersError({ error }))),
          startWith(UserPageSimilarCustomersActions.searchSimilarCustomersStarted()),
        );
      }),
    );
}, { functional: true, dispatch: true });

export const mergeButtonClicked = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
      ofType(UserPageSimilarCustomersActions.mergeButtonClicked),
      concatLatestFrom(() => store.select(userPageSimilarCustomersFeature.selectSourceUser)),
      filter(([ _action, sourceUser ]) => Boolean(sourceUser)),
      map(([ { user: duplicate }, sourceUser ]) => {
        const { company, billingAddress } = detectMergeConflicts(sourceUser, duplicate);

        return UserPageMergeConflictDialogActions.dialogOpened({
            primaryId: sourceUser.id,
            duplicateId: duplicate.id,
            company,
            billingAddress,
        });
      }),
    );
}, { functional: true, dispatch: true });
